import React, { useMemo, useState } from "react";
import { deleteDoc, doc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useFamilyData } from "../../hooks/useFamilyData";
import { logAudit } from "../../utils/audit";
import { useToast } from "../../components/ui/ToastProvider";
import { useConfirm } from "../../components/ui/ConfirmProvider";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
];

const normalizeRepeat = (task) => {
  if (!task) return null;
  if (typeof task.repeat === "string") return { type: task.repeat };
  if (task.repeat && typeof task.repeat === "object") return task.repeat;
  return null;
};

const formatRepeatLabel = (task) => {
  const repeat = normalizeRepeat(task);
  const type = repeat?.type || "daily";
  if (type === "weekly" && typeof repeat?.day === "number") {
    return `Weekly (${DAY_LABELS[repeat.day] || "?"})`;
  }
  if (type === "custom" && Array.isArray(repeat?.days)) {
    const labels = repeat.days.map((day) => DAY_LABELS[day]).filter(Boolean);
    return labels.length > 0 ? `Custom (${labels.join(", ")})` : "Custom";
  }
  if (type === "weekdays") return "Weekdays";
  if (type === "weekends") return "Weekends";
  if (type === "monthly" && typeof repeat?.date === "number") {
    return `Monthly (${repeat.date})`;
  }
  return type[0].toUpperCase() + type.slice(1);
};

export default function RepeatChoresCard({ familyId, canManage }) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { tasks = [], kids = [] } = useFamilyData(familyId);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: "",
    points: 0,
    assignedTo: "unassigned",
    repeatType: "daily",
    customDays: [],
    repeatDay: 1,
    showAfterTime: "",
    expiryTime: ""
  });

  const repeatTasks = useMemo(() => {
    return tasks
      .filter(
        (task) =>
          task.frequency === "repeat" ||
          (task.repeat && (task.repeat.type || typeof task.repeat === "string"))
      )
      .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  }, [tasks]);

  const kidsById = useMemo(() => {
    return kids.reduce((acc, kid) => {
      acc[kid.id] = kid.name || "Kid";
      return acc;
    }, {});
  }, [kids]);

  const startEdit = (task) => {
    const repeat = normalizeRepeat(task);
    setEditingId(task.id);
    setForm({
      title: task.title || "",
      points: Number(task.points || 0),
      assignedTo: task.assignedTo || "unassigned",
      repeatType: repeat?.type || "daily",
      customDays: Array.isArray(repeat?.days) ? repeat.days : [],
      repeatDay: typeof repeat?.day === "number" ? repeat.day : 1,
      showAfterTime: task.showAfterTime || "",
      expiryTime: task.expiryTime || ""
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const updateTask = async (task) => {
    if (!canManage) return;
    const title = form.title.trim();
    if (!title) return;
    const repeat = normalizeRepeat(task);
    const nextRepeat = { type: form.repeatType };
    if (Array.isArray(repeat?.exceptions) && repeat.exceptions.length > 0) {
      nextRepeat.exceptions = repeat.exceptions;
    }
    if (form.repeatType === "custom") nextRepeat.days = form.customDays;
    if (form.repeatType === "weekly") nextRepeat.day = form.repeatDay;
    if (form.repeatType === "monthly" && typeof repeat?.date === "number") {
      nextRepeat.date = repeat.date;
    }

    await updateDoc(doc(db, "families", familyId, "tasks", task.id), {
      title,
      points: Number(form.points) || 0,
      assignedTo: form.assignedTo,
      frequency: "repeat",
      repeat: nextRepeat,
      showAfterTime: form.showAfterTime || null,
      expiryTime: form.expiryTime || null
    });
    await logAudit({
      familyId,
      action: "update",
      entity: "repeatTask",
      meta: { taskId: task.id, title }
    });
    setEditingId(null);
  };

  const deleteTask = async (task) => {
    if (!canManage) return;
    const ok = await confirm({
      title: "Delete repeating chore?",
      message: `Delete "${task.title}"? This cannot be undone.`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      danger: true
    });
    if (!ok) return;
    const snapshot = { ...task };
    await deleteDoc(doc(db, "families", familyId, "tasks", task.id));
    await logAudit({
      familyId,
      action: "delete",
      entity: "repeatTask",
      meta: { taskId: task.id, title: task.title }
    });
    showToast({
      message: `Deleted "${task.title}".`,
      actionLabel: "Undo",
      onAction: async () => {
        await setDoc(doc(db, "families", familyId, "tasks", task.id), snapshot, { merge: true });
        await logAudit({
          familyId,
          action: "restore",
          entity: "repeatTask",
          meta: { taskId: task.id, title: task.title }
        });
      }
    });
  };

  return (
    <div className="glass parent-card" data-tour="parent-repeats-card">
      <h2>Repeating Chores</h2>
      <div className="card-body">
        {!canManage && (
          <p style={{ textAlign: "center", opacity: 0.6 }}>
            You have view-only access.
          </p>
        )}
        {repeatTasks.length === 0 && (
          <div style={{ textAlign: "center", opacity: 0.75, display: "grid", gap: 8 }}>
            <p style={{ margin: 0 }}>No repeating chores yet.</p>
            <button
              className="btn-nav"
              type="button"
              onClick={() =>
                document.querySelector("[data-tour='parent-task-card']")?.scrollIntoView({ behavior: "smooth", block: "center" })
              }
            >
              Create one in Assign Chores
            </button>
          </div>
        )}
        {repeatTasks.map((task) => {
          const isEditing = editingId === task.id;
          const assigneeLabel =
            task.assignedTo === "unassigned"
              ? "Up for Grabs"
              : kidsById[task.assignedTo] || "Unknown";
          return (
            <div
              key={task.id}
              style={{
                borderTop: "1px solid rgba(0,0,0,0.08)",
                paddingTop: "12px",
                marginTop: "12px"
              }}
            >
              {!isEditing && (
                <div style={{ display: "grid", gap: "6px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                    <strong>{task.title || "Untitled chore"}</strong>
                    <span style={{ opacity: 0.7 }}>{formatRepeatLabel(task)}</span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", opacity: 0.75 }}>
                    <span>Pts: {task.points || 0}</span>
                    <span>Assigned: {assigneeLabel}</span>
                    {task.showAfterTime && <span>Show: {task.showAfterTime}</span>}
                    {task.expiryTime && <span>Due: {task.expiryTime}</span>}
                  </div>
                  {canManage && (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button className="btn-nav" onClick={() => startEdit(task)}>
                        Edit
                      </button>
                      <button
                        className="btn-nav"
                        style={{ color: "var(--danger)", fontWeight: 600 }}
                        onClick={() => deleteTask(task)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              )}
              {isEditing && (
                <div style={{ display: "grid", gap: "8px" }}>
                  <input
                    placeholder="Chore name"
                    value={form.title}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                  />
                  <div style={{ display: "flex", gap: "10px" }}>
                    <input
                      type="number"
                      placeholder="Pts"
                      value={form.points}
                      onChange={(e) => setForm((prev) => ({ ...prev, points: e.target.value }))}
                      style={{ width: "80px" }}
                    />
                    <select
                      value={form.assignedTo}
                      onChange={(e) => setForm((prev) => ({ ...prev, assignedTo: e.target.value }))}
                      style={{ flex: 1 }}
                    >
                      <option value="unassigned">Up for Grabs</option>
                      {kids.map((kid) => (
                        <option key={kid.id} value={kid.id}>
                          {kid.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <select
                    value={form.repeatType}
                    onChange={(e) => setForm((prev) => ({ ...prev, repeatType: e.target.value }))}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekdays">Weekdays</option>
                    <option value="weekends">Weekends</option>
                    <option value="weekly">Weekly</option>
                    <option value="custom">Custom</option>
                  </select>
                  {form.repeatType === "weekly" && (
                    <select
                      value={form.repeatDay}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, repeatDay: Number(e.target.value) }))
                      }
                    >
                      {DAY_NAMES.map((name, idx) => (
                        <option key={name} value={idx}>
                          {name}
                        </option>
                      ))}
                    </select>
                  )}
                  {form.repeatType === "custom" && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px" }}>
                      {DAY_LABELS.map((day, idx) => {
                        const active = form.customDays.includes(idx);
                        return (
                          <button
                            key={day}
                            className="btn-nav"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                customDays: prev.customDays.includes(idx)
                                  ? prev.customDays.filter((d) => d !== idx)
                                  : [...prev.customDays, idx]
                              }))
                            }
                            style={{
                              background: active ? "var(--accent)" : "var(--surface)",
                              color: active ? "white" : "inherit"
                            }}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: "0.8rem", opacity: 0.7 }}>Show after</label>
                      <input
                        type="time"
                        value={form.showAfterTime}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, showAfterTime: e.target.value }))
                        }
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: "0.8rem", opacity: 0.7 }}>Due by</label>
                      <input
                        type="time"
                        value={form.expiryTime}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, expiryTime: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button className="btn-nav success-btn" onClick={() => updateTask(task)}>
                      Save
                    </button>
                    <button className="btn-nav" onClick={cancelEdit}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
